import React, { ReactNode, forwardRef } from 'react';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Slide,
  Chip,
  alpha,
} from '@mui/material';

import { TransitionProps } from '@mui/material/transitions';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { State } from '@store/index';

export const IconBadge = styled.div<{ background?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px;
  height: 40px;
  width: 40px;
  background-color: ${({ theme, background }) => background || theme.primary};
  color: white;
`;

export const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction='down' ref={ref} {...props} />;
});

interface Props {
  // if modal is open
  open: boolean;
  // the title of the modal
  header: string;
  // the actions to be displayed beside the header
  header_actions?: ReactNode;
  // an icon beside the header in a badge, should be a material-icon
  icon?: ReactNode;
  // the background color of the icon badge
  background?: string;
  // a unique identifier that can be set to add a chip in the top right of the modal
  identifier?: string | null;
  // a secondary unique identifier that can be set to add a chip beside the identifier
  secondary_identifier?: string | null;
  // a paragraph section beneath the title for adding information about what the modal is for
  info?: string | ReactNode;
  // the body content of the modal
  body?: ReactNode;
  // the footer content of the modal
  actions?: ReactNode;
  // if true, the border beneath the body will not be displayed
  noBottomBorder?: boolean;
  // if true, the padding beneath the body will not be displayed
  noPaddingBottom?: boolean;
  // current page number, can be used with total pages to specify a modal with multiple pages
  page?: number;
  // the total number of pages this modal has
  total_pages?: number;
  // a function to run when closing the modal
  onClose?: () => void;
  // a function for opening or closing the modal
  setOpen: (val?: any) => void;
  // a function for keypress events
  onKeyDown?: (e: React.KeyboardEvent) => void;
  // a function for onBlur events
  onBlur?: () => void;
  // set the min width of the modal
  minWidth?: string;
  // set the max width of the modal
  maxWidth?: string;
}

export const Modal: React.FC<Props> = ({
  header,
  header_actions,
  identifier,
  secondary_identifier,
  body,
  actions,
  info,
  icon,
  background,
  open,
  page,
  total_pages,
  setOpen,
  onClose,
  noBottomBorder,
  onKeyDown,
  onBlur,
  noPaddingBottom,
  minWidth,
  maxWidth,
}) => {
  const theme = useSelector((state: State) => state.flow.theme);
  return (
    <Dialog
      data-intercom-target='Modal Overlay'
      TransitionComponent={Transition}
      open={Boolean(open)}
      fullWidth
      maxWidth='md'
      onClose={() => (typeof onClose === 'function' ? onClose() : setOpen(null))}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      PaperProps={{
        style: {
          backgroundColor: '#0f141b',
          minWidth: minWidth ? minWidth : undefined,
          maxWidth: maxWidth ? maxWidth : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 32px' }}
      >
        <Stack direction='row' alignItems='center' spacing={2}>
          <IconBadge
            background={
              theme === 'dark' && background === 'primary'
                ? alpha('#FFFFFF', 0.16)
                : background === 'primary'
                ? 'black'
                : background
            }
          >
            {icon && icon}
          </IconBadge>
          <h3>{header}</h3>
        </Stack>
        <Stack direction='row' alignItems='center' spacing={1}>
          {secondary_identifier && <Chip sx={{ borderRadius: '6px' }} label={secondary_identifier} />}
          {identifier && <Chip sx={{ borderRadius: '6px' }} label={identifier} />}
          {page && total_pages && <Chip sx={{ borderRadius: '6px' }} label={`${page} / ${total_pages}`} />}
          {header_actions}
        </Stack>
      </DialogTitle>
      {info && !body && <Divider />}
      {info && (
        <DialogContentText
          sx={{ padding: '24px 32px', paddingTop: info && !body ? '24px' : 0, whiteSpace: 'pre-wrap !important' }}
        >
          {info}
        </DialogContentText>
      )}
      {info && body && <Divider />}
      {body ? <DialogContent sx={{ padding: '24px 32px' }}>{body}</DialogContent> : info && <Divider />}
      {body && !noBottomBorder && <Divider />}
      <DialogActions sx={noPaddingBottom ? { padding: '4px 32px' } : { padding: '24px 32px' }}>{actions}</DialogActions>
    </Dialog>
  );
};
